import { z } from "zod";

import { zTipoNumero, type StatusNumero, type TipoNumero } from "./enums";

/**
 * Representação pública de um número (painel e API `/v1`). Serializada por
 * `NumeroMapper.paraResumo` (apps/api/src/numeros/base). NUNCA inclui token,
 * App Secret nem a PK.
 */
export interface NumeroResumo {
  id: string; // publicId
  tipo: TipoNumero;
  apelido: string;
  status: StatusNumero;
  /** E.164 sem "+". Null até conectar (não oficial) / validar (oficial). */
  telefone: string | null;
  nomePerfil: string | null;
  /** Só NAO_OFICIAL: cadência de saída (mensagens/minuto). */
  ritmoPorMinuto: number | null;
  criadoEm: string;
  ultimaAtividadeEm: string | null;
  oficial: NumeroOficialDetalhes | null;
  /** `protegidoPorProxy`: a conexão sai por um proxy (o cliente não vê host nem credenciais). */
  naoOficial: { conectadoEm: string | null; protegidoPorProxy: boolean } | null;
}

export interface NumeroOficialDetalhes {
  wabaId: string;
  phoneNumberId: string;
  tokenFim4: string;
  qualityRating: string | null;
  nomeVerificado: string | null;
  /** WABA sem forma de pagamento válida (informativo; não bloqueia). */
  alertaPagamento: boolean;
  statusMotivo: string | null;
  vazaoPorSegundo: number;
  /** Callback para cadastrar no App do cliente na Meta. */
  urlWebhook: string;
  verifyToken: string;
}

// ─── Entradas (zod é a fonte de verdade; a API valida com ZodPipe) ───────────

const apelido = z
  .string({ required_error: "Informe um apelido." })
  .trim()
  .min(1, "Informe um apelido.")
  .max(60, "O apelido pode ter no máximo 60 caracteres.");

/** Telefone com DDI, aceita máscara ("+55 (11) 98765-4321"); normaliza para só dígitos (E.164 sem "+"). */
export const zTelefoneE164 = z
  .string({ required_error: "Informe o telefone com DDI." })
  .transform((v) => v.replace(/\D/g, ""))
  .refine((v) => v.length >= 10 && v.length <= 15, "Informe o telefone com DDI e DDD (10 a 15 dígitos).");

/** `POST /numeros/nao-oficial`. */
export const CriarNumeroNaoOficialSchema = z.object({ apelido });
export type CriarNumeroNaoOficialInput = z.infer<typeof CriarNumeroNaoOficialSchema>;

/**
 * `PATCH /numeros/:id`. `tipo` é aceito SÓ para ser recusado (422 `tipo_imutavel`):
 * o tipo é definitivo; para trocar, remova o número e cadastre de novo.
 */
export const AtualizarNumeroSchema = z.object({
  apelido: apelido.optional(),
  /** Só NAO_OFICIAL: cadência de saída em mensagens por minuto. */
  ritmoPorMinuto: z
    .number({ invalid_type_error: "O ritmo deve ser um número." })
    .int("O ritmo deve ser um número inteiro.")
    .min(1, "O ritmo mínimo é 1 mensagem por minuto.")
    .max(600, "O ritmo máximo é 600 mensagens por minuto.")
    .optional(),
  tipo: zTipoNumero.optional(),
});
export type AtualizarNumeroInput = z.infer<typeof AtualizarNumeroSchema>;

/** `POST /numeros/:id/conectar`. Sem `telefone` = QR Code; com `telefone` = código de pareamento. */
export const ConectarNumeroSchema = z.object({ telefone: zTelefoneE164.optional() });
export type ConectarNumeroInput = z.infer<typeof ConectarNumeroSchema>;

/** `DELETE /numeros/:id`: confirmação destrutiva digitando o apelido (corpo ou querystring). */
export const RemoverNumeroSchema = z.object({
  confirmacaoApelido: z.string({ required_error: "Digite o apelido do número para confirmar." }),
});
export type RemoverNumeroInput = z.infer<typeof RemoverNumeroSchema>;

// ─── Saídas ─────────────────────────────────────────────────────────────────

export interface UsoPlano {
  usados: number;
  limite: number;
}

/** `GET /numeros`. */
export interface ListaNumeros {
  numeros: NumeroResumo[];
  uso: UsoPlano;
}

/**
 * Resposta de `POST /numeros/:id/conectar`.
 *  - `modo: "QR"`: `qrcode` (data URL PNG) e `codigo` (texto do QR, para renderizar no cliente). Podem vir nulos se o
 *    Evolution ainda não gerou o primeiro QR: ele chega logo em seguida pelo evento `numero.qrcode`.
 *  - `modo: "CODIGO"`: `codigoPareamento` (8 caracteres) para digitar no WhatsApp (Aparelhos conectados > Conectar com número).
 * `expiraEm`: depois disso o QR deixa de ser renovado (2 min); chame `conectar` de novo para "Gerar novo QR".
 */
export interface ConexaoNumero {
  numero: NumeroResumo;
  modo: "QR" | "CODIGO";
  qrcode: string | null;
  codigo: string | null;
  codigoPareamento: string | null;
  expiraEm: string;
}

/** Dados do evento tempo real/saída `numero.status` (ids públicos). */
export interface EventoNumeroStatus {
  numeroId: string;
  tipo: TipoNumero;
  apelido: string;
  status: StatusNumero;
  telefone: string | null;
  nomePerfil: string | null;
  /** Presente quando a mudança tem causa a explicar (ex.: "telefone_em_uso", "logout", "instancia_ausente"). */
  motivo?: string;
  ocorridoEm: string;
}

/**
 * Dados do evento `numero.qrcode`. `qrcode` (PNG em data URL) só vai pelo tempo real quando cabe no NOTIFY do
 * Postgres (~8 KB); `codigo` (texto do QR) sempre vai e permite renderizar o QR no cliente.
 */
export interface EventoNumeroQrcode {
  numeroId: string;
  qrcode?: string;
  codigo: string;
  expiraEm: string;
}
